import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getApiErrorMessage } from '@/api/apiErrors';
import { passwordResetConfirm, passwordResetRequest } from '@/api/apiAuth';
import { EmailAddressIcon } from '@/assets/icons/onboarding/icons';
import { EyeIcon, PasswordIcon } from '@/assets/icons/onboarding/icons';
import { InputForm } from '@/components/CustomForm/components';
import { Button } from '@/components/common/Button';
import { LoginLayout } from '@/components/layout/LoginLayout';
import '@/pages/auth/password-reset.scss';

type ForgotPasswordValues = {
  email: string;
};

type PasswordResetValues = {
  new_password: string;
  password2: string;
};

function CheckCircleIcon() {
  return (
    <svg
      className="forgot-password-success-icon"
      viewBox="-2 -2 125.1 125.1"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable={false}
    >
      <circle cx="60.55" cy="60.55" r="60.55" fill="#00B48F" />
      <path
        d="M35.3 61.7L52.9 79.4L85.7 45.4"
        stroke="#ffffff"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PasswordResetPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resetToken = searchParams.get('token')?.trim() ?? '';
  const isPasswordUpdateRoute = location.pathname === '/password-reset';
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordValues>({
    defaultValues: { email: '' },
    mode: 'onChange',
  });

  const {
    register: registerReset,
    handleSubmit: handleResetSubmit,
    watch,
    formState: { errors: resetErrors, isValid: isResetValid },
  } = useForm<PasswordResetValues>({
    defaultValues: {
      new_password: '',
      password2: '',
    },
    mode: 'onChange',
  });
  const newPasswordValue = watch('new_password');
  const confirmPasswordValue = watch('password2');

  const onSubmit = handleSubmit(async (values) => {
    setIsSubmitting(true);
    try {
      await passwordResetRequest({ email: values.email });
      setSubmitted(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('auth.resetRequestError')));
    } finally {
      setIsSubmitting(false);
    }
  });

  const onSubmitReset = handleResetSubmit(async (values) => {
    if (!resetToken) {
      toast.error(t('auth.resetMissingTokenBody'));
      return;
    }

    setIsSubmitting(true);
    try {
      await passwordResetConfirm({
        token: resetToken,
        new_password: values.new_password,
      });
      toast.success(t('auth.resetCompleted'));
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('auth.resetConfirmError')));
    } finally {
      setIsSubmitting(false);
    }
  });

  if (isPasswordUpdateRoute) {
    if (!resetToken) {
      return (
        <LoginLayout
          title=""
          portalTitle={t('auth.resetMissingTokenTitle')}
          portalDescription={t('auth.resetMissingTokenBody')}
          portalDescriptionClassName="forgot-password-portal-description"
        >
          <div className="stack password-reset-missing-token">
            <Button className="button--block" variant="onboarding" tone="primary" onClick={() => navigate('/forgot-password')}>
              {t('auth.forgotPassword')}
            </Button>
            <Link className="forgot-password-signin-link" to="/login">
              {t('auth.backToLogin')}
            </Link>
          </div>
        </LoginLayout>
      );
    }

    return (
      <LoginLayout
        title=""
        portalTitle={t('auth.resetPasswordTitle')}
        portalDescription={t('auth.resetPasswordDescription')}
        portalDescriptionClassName="forgot-password-portal-description"
      >
        <form className="stack auth-form password-reset-form" onSubmit={onSubmitReset}>
          <InputForm
            className="field--onboarding"
            type="password"
            placeholder={t('auth.passwordPlaceholder')}
            autoComplete="new-password"
            variant="onboarding"
            leftIcon={<PasswordIcon className="onboarding-icon" />}
            rightIcon={<EyeIcon className="onboarding-icon" />}
            title={newPasswordValue ? t('auth.passwordRulesHint') : undefined}
            error={resetErrors.new_password?.message}
            {...registerReset('new_password', {
              required: t('auth.passwordRequired'),
              minLength: {
                value: 8,
                message: t('auth.passwordMinLength'),
              },
            })}
          />
          <InputForm
            className="field--onboarding"
            type="password"
            placeholder={t('auth.confirmPasswordPlaceholder')}
            autoComplete="new-password"
            variant="onboarding"
            leftIcon={<PasswordIcon className="onboarding-icon" />}
            rightIcon={<EyeIcon className="onboarding-icon" />}
            title={confirmPasswordValue ? t('auth.passwordRulesHint') : undefined}
            error={resetErrors.password2?.message}
            {...registerReset('password2', {
              required: t('auth.confirmPasswordRequired'),
              validate: (value, formValues) => value === formValues.new_password || t('auth.passwordsMustMatch'),
            })}
          />
          <Button className="password-reset-submit button--block" variant="onboarding" type="submit" disabled={isSubmitting || !isResetValid}>
            {t('auth.resetPasswordSubmit')}
          </Button>
          <p className="forgot-password-back-copy">
            {t('auth.backToLogin')}{' '}
            <Link className="forgot-password-signin-link" to="/login">
              {t('auth.signInLink')}
            </Link>
          </p>
        </form>
      </LoginLayout>
    );
  }

  if (submitted) {
    return (
      <LoginLayout title="" portalTitle="">
        <div className="stack forgot-password-success">
          <CheckCircleIcon />
          <p className="forgot-password-success-message">
            {t('auth.forgotPasswordSuccessMessage')}
          </p>
          <div className="forgot-password-success-actions">
            <Button
              variant="onboarding"
              tone="secondary"
              className="forgot-password-resend-btn"
              onClick={() => setSubmitted(false)}
            >
              {t('auth.forgotPasswordResend')}
            </Button>
            <Button
              variant="onboarding"
              tone="primary"
              className="forgot-password-signin-btn"
              onClick={() => navigate('/login')}
            >
              {t('auth.forgotPasswordSignInNow')}
            </Button>
          </div>
        </div>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout
      title=""
      portalTitle={t('auth.forgotPasswordTitle')}
      portalDescription={t('auth.forgotPasswordDescription')}
      portalDescriptionClassName="forgot-password-portal-description"
    >
      <form className="stack auth-form forgot-password-form" onSubmit={onSubmit}>
        <InputForm
          className="forgot-password-email field--onboarding"
          type="email"
          placeholder={t('auth.email')}
          autoComplete="email"
          variant="onboarding"
          leftIcon={<EmailAddressIcon className="onboarding-icon" />}
          error={errors.email?.message}
          {...register('email', {
            required: t('auth.forgotPasswordEmailRequired'),
          })}
        />

        <Button className="forgot-password-submit button--block" variant="onboarding" type="submit" disabled={isSubmitting || !isValid}>
          {t('auth.forgotPasswordSendLink')}
        </Button>

        <p className="forgot-password-back-copy">
          {t('auth.backToLogin')}{' '}
          <Link className="forgot-password-signin-link" to="/login">
            {t('auth.signInLink')}
          </Link>
        </p>
      </form>
    </LoginLayout>
  );
}